import React from 'react';
import { Link } from 'react-router-dom';
import { Home, RefreshCw } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const NotFound = () => {
  const { theme } = useTheme();
  
  // Adding inline styles for elements that need direct theme values
  const styles = {
    container: {
      backgroundColor: theme.background,
      color: theme.text
    },
    decorativeCircle: {
      backgroundColor: theme.primary
    },
    divider: {
      backgroundColor: theme.primary
    },
    homeButton: {
      backgroundColor: theme.primary,
      color: theme.text,
    },
    homeButtonHover: {
      backgroundColor: theme.primaryHover
    },
    backButton: {
      color: theme.primary,
      borderColor: theme.primary
    },
    backButtonHover: {
      backgroundColor: theme.secondary
    }
  };
  
  return (
    <div 
      className="flex flex-col items-center justify-center min-h-screen px-4 text-center theme-transition"
      style={styles.container}
    >
      <div className="space-y-8 max-w-md">
        {/* Animated 404 Number */}
        <div className="relative">
          <h1 className="text-8xl md:text-9xl font-bold text-primary animate-pulse">404</h1>
          <div 
            className="absolute -top-10 -right-10 w-24 h-24 md:w-32 md:h-32 rounded-full opacity-50 animate-ping-slow" 
            style={styles.decorativeCircle}
          ></div>
          <div 
            className="absolute -bottom-6 -left-6 w-16 h-16 rounded-full opacity-30 animate-bounce"
            style={styles.decorativeCircle}
          ></div>
        </div>
        
        <h2 className="text-2xl md:text-3xl font-semibold text-theme">Oops! Page Not Found</h2>
        
        <p className="text-accent mt-2 text-lg">
          We couldn't find the page you're looking for. It might have been moved or doesn't exist.
        </p>
        
        <div 
          className="w-24 h-1 mx-auto my-6 rounded-full opacity-70"
          style={styles.divider}
        ></div>
        
        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 justify-center mt-8">
          <Link 
            to="/" 
            className="inline-flex items-center justify-center px-6 py-3 rounded-lg transition-all transform hover:scale-105"
            style={styles.homeButton}
            onMouseOver={e => e.currentTarget.style.backgroundColor = theme.primaryHover}
            onMouseOut={e => e.currentTarget.style.backgroundColor = theme.primary}
          >
            <Home size={20} className="mr-2" />
            Back to Home
          </Link>
          
          <button 
            onClick={() => window.history.back()} 
            className="inline-flex items-center justify-center px-6 py-3 border rounded-lg transition-all transform hover:scale-105"
            style={styles.backButton}
            onMouseOver={e => {
              e.currentTarget.style.backgroundColor = theme.secondary;
            }}
            onMouseOut={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <RefreshCw size={20} className="mr-2" />
            Go Back
          </button>
        </div>
      </div>
      
      {/* Additional styling for the background */}
      <div className="fixed -z-10 inset-0 overflow-hidden opacity-5">
        <div 
          className="absolute -top-10 -right-10 w-64 h-64 rounded-full"
          style={styles.decorativeCircle}
        ></div>
        <div 
          className="absolute top-1/4 left-1/4 w-32 h-32 rounded-full"
          style={styles.decorativeCircle}
        ></div>
        <div 
          className="absolute bottom-1/3 right-1/3 w-48 h-48 rounded-full"
          style={styles.decorativeCircle}
        ></div>
        <div 
          className="absolute -bottom-10 -left-10 w-72 h-72 rounded-full"
          style={styles.decorativeCircle}
        ></div>
      </div>
    </div>
  );
};

// Add a CSS animation for the ping-slow effect
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.innerHTML = `
    @keyframes ping-slow {
      0% {
        transform: scale(1);
        opacity: 0.5;
      }
      50% {
        transform: scale(1.1);
        opacity: 0.3;
      }
      100% {
        transform: scale(1);
        opacity: 0.5;
      }
    }
    .animate-ping-slow {
      animation: ping-slow 3s ease-in-out infinite;
    }
    
    /* Add theme transition for smooth theme changes */
    .theme-transition {
      transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
    }
  `;
  document.head.appendChild(style);
}

export default NotFound; 